import React, { useState } from "react";
import { TextField, Button } from "@merge-api/merge-javascript-shared";
import { Sparkles, Loader2 } from "lucide-react";
import { getGeneratedAIFieldMapping } from "../../../../../../../../../api-client/APIClient";
import useAppContext from "../../../../../../../../context/useAppContext";
import { LinkedAccount } from "../../../../../../../../../models/Entities";
import { getExpressionLabel } from "../../../../utils/expressionUtils";

interface AIGeneratedFieldMappingInputProps {
  jsonData: string;
  handleJsonAdvancedMappingExpressionInput: (jsonAdvancedMappingExpression: string) => void;
  linkedAccount?: LinkedAccount | null;
  remoteField?: string | null;
  onEnter: () => void;
  isLoading?: boolean;
  setIsLoading?: (loading: boolean) => void;
  /** Controlled prompt value (optional - for persistence across mode switches) */
  promptValue?: string;
  /** Controlled prompt onChange (optional - for persistence across mode switches) */
  onPromptChange?: (value: string) => void;
}

const AIGeneratedFieldMappingInput = ({
  jsonData,
  handleJsonAdvancedMappingExpressionInput,
  linkedAccount,
  remoteField,
  onEnter,
  isLoading: externalLoading,
  setIsLoading: externalSetIsLoading,
  promptValue,
  onPromptChange,
}: AIGeneratedFieldMappingInputProps) => {
  const [internalValue, setInternalValue] = useState("");
  // Use controlled value if provided, otherwise use internal state
  const value = promptValue !== undefined ? promptValue : internalValue;
  const setValue = (newValue: string) => {
    if (onPromptChange !== undefined) {
      onPromptChange(newValue);
    } else {
      setInternalValue(newValue);
    }
  };
  const [internalLoading, setInternalLoading] = useState(false);
  const loading = externalLoading !== undefined ? externalLoading : internalLoading;
  const setLoading = externalSetIsLoading !== undefined ? externalSetIsLoading : setInternalLoading;
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const { user } = useAppContext();
  const organizationId = user?.organization.id;
  const isJmesPathDeprecationEnabled = user.is_jmes_path_deprecation_dashboard_enabled ?? false;

  const handleSubmit = () => {
    setLoading(true);
    setErrorMessage(undefined);
    onEnter();
    getGeneratedAIFieldMapping(
      {
        organization_id: organizationId,
        fm_generation_prompt: value,
        provided_remote_data_json: jsonData,
        logging_metadata: {
          linked_account_id: linkedAccount?.id,
          remote_field: remoteField || "",
        },
      },
      (data: any) => {
        const expression = data.advanced_mapping_expression ?? data.jmes_path;
        if (expression != null) {
          handleJsonAdvancedMappingExpressionInput(expression);
        }
        setLoading(false);
      },
      (message?: string) => {
        setErrorMessage(
          message ||
            `Please enter a valid prompt to generate a ${getExpressionLabel(
              isJmesPathDeprecationEnabled,
            )} expression.`,
        );
        setLoading(false);
      },
    );
  };

  const postfix = (
    <Button
      onClick={handleSubmit}
      disabled={!value.trim() || loading}
      className="mr-3 mt-16 px-3 py-1.5 gap-1.5"
      size="sm"
    >
      {loading ? (
        <Loader2 size={12} className="bg-transparent text-current animate-spin" />
      ) : (
        <Sparkles size={12} className="bg-transparent text-current" />
      )}
      Generate
    </Button>
  );

  return (
    <div className={`mt-3${loading ? " pointer-events-none" : ""}`}>
      <TextField
        placeholder="Describe the data you want to map..."
        value={value}
        error={!!errorMessage}
        errorText={errorMessage}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (value.trim() && !loading) {
              handleSubmit();
            }
          }
        }}
        postfix={postfix}
        multiline
        rows={4}
        inputClassName="resize-none"
      />
    </div>
  );
};

export default AIGeneratedFieldMappingInput;
